import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, ReferenceArea, Legend, Area, AreaChart
} from 'recharts';
import {
  Download, AlertCircle, TrendingDown, TrendingUp, Eye, EyeOff,
  Calendar, BarChart3, Loader2
} from 'lucide-react';
import { useLabResults } from '../../lib/hooks/useLabResults';
import { useFEV1Trends } from '../../lib/hooks/useTrends';

interface LabResultsTrendsProps {
  patientId: string;
  patientName: string;
}

export function LabResultsTrends({ patientId, patientName }: LabResultsTrendsProps) {
  const [viewMode, setViewMode] = useState<'chart' | 'table'>('chart');
  const [showTacrolimusDose, setShowTacrolimusDose] = useState(true);
  const [showEverolimusDose, setShowEverolimusDose] = useState(true);

  // Fetch lab results from backend
  const { data: labResults, isLoading: labLoading } = useLabResults(parseInt(patientId));
  
  // Fetch FEV1 trends from backend
  const { data: fev1TrendsData, isLoading: fev1Loading } = useFEV1Trends(parseInt(patientId), 6);

  // Helper function to filter and transform lab results by test name
  const filterLabData = (testName: string, options?: { checkAbnormal?: boolean; threshold?: number }) => {
    if (!labResults) return [];
    
    return labResults
      .filter(result => result.test_name.toLowerCase().includes(testName.toLowerCase()))
      .sort((a, b) => new Date(a.test_date).getTime() - new Date(b.test_date).getTime())
      .map(result => {
        const value = parseFloat(result.result_value || '0');
        let extraData = {};
        
        if (options?.checkAbnormal && options.threshold) {
          extraData = { isAbnormal: result.abnormal_flag || false };
        }
        
        return {
          date: result.test_date,
          value,
          ...extraData
        };
      });
  };

  // Transform lab data to chart format
  const hemoglobinData = useMemo(() => {
    return filterLabData('hemoglobin');
  }, [labResults]);

  const wbcData = useMemo(() => {
    return filterLabData('wbc', { checkAbnormal: true, threshold: 11000 }).map(item => ({
      ...item,
      isAbnormal: item.value > 11000 || item.value < 4000
    }));
  }, [labResults]);

  const creatinineData = useMemo(() => {
    return filterLabData('creatinine').map(item => ({
      ...item,
      isHigh: item.value > 1.5
    }));
  }, [labResults]);

  const tacrolimusData = useMemo(() => {
    if (!labResults) return [];
    
    return labResults
      .filter(result => 
        result.test_type === 'drug_level' && 
        result.test_name.toLowerCase().includes('tacrolimus')
      )
      .sort((a, b) => new Date(a.test_date).getTime() - new Date(b.test_date).getTime())
      .map(result => ({
        date: result.test_date,
        level: parseFloat(result.result_value || '0'),
        dose: result.notes ? parseFloat(result.notes.match(/\d+\.?\d*/)?.[0] || '3') : 3
      }));
  }, [labResults]);

  const everolimusData = useMemo(() => {
    if (!labResults) return [];
    
    return labResults
      .filter(result => 
        result.test_type === 'drug_level' && 
        result.test_name.toLowerCase().includes('everolimus')
      )
      .sort((a, b) => new Date(a.test_date).getTime() - new Date(b.test_date).getTime())
      .map(result => ({
        date: result.test_date,
        level: parseFloat(result.result_value || '0'),
        dose: result.notes ? parseFloat(result.notes.match(/\d+\.?\d*/)?.[0] || '1.5') : 1.5
      }));
  }, [labResults]);

  const fev1Data = useMemo(() => {
    if (!fev1TrendsData?.data) return [
      { date: '2025-09-01', value: 86, baseline: 85 },
      { date: '2025-09-08', value: 84, baseline: 85 },
      { date: '2025-09-15', value: 82, baseline: 85 },
      { date: '2025-09-22', value: 78, baseline: 85 },
      { date: '2025-09-29', value: 76, baseline: 85 },
      { date: '2025-10-06', value: 78, baseline: 85 },
      { date: '2025-10-13', value: 78, baseline: 85 },
    ];
    
    const baseline = fev1TrendsData.data[0]?.actual || fev1TrendsData.data[0]?.predicted || 85;
    return fev1TrendsData.data.map(item => ({
      date: item.date,
      value: item.actual || item.predicted,
      baseline
    }));
  }, [fev1TrendsData]);

  const cfDNAData = useMemo(() => {
    const data = filterLabData('cfdna').map(item => ({
      ...item,
      isElevated: item.value > 1.0
    }));
    return data.length > 0 ? data : [
      { date: '2025-09-01', value: 0.6, isElevated: false },
      { date: '2025-09-08', value: 0.7, isElevated: false },
      { date: '2025-09-15', value: 0.8, isElevated: false },
      { date: '2025-09-22', value: 1.2, isElevated: true },
      { date: '2025-09-29', value: 1.4, isElevated: true },
      { date: '2025-10-06', value: 0.9, isElevated: false },
      { date: '2025-10-13', value: 0.7, isElevated: false },
    ];
  }, [labResults]);

  // ✅ FIX: Check if data exists before accessing properties
  const latestWBC = wbcData[wbcData.length - 1] || { value: 0 };
  const previousWBC = wbcData[wbcData.length - 2];
  const wbcChange = (previousWBC && latestWBC.value) 
    ? ((latestWBC.value - previousWBC.value) / previousWBC.value * 100).toFixed(1) 
    : '0';

  const latestFEV1 = fev1Data[fev1Data.length - 1] || { value: 0 };
  const previousFEV1 = fev1Data[fev1Data.length - 2];
  const fev1Change = (previousFEV1 && latestFEV1.value) 
    ? ((latestFEV1.value - previousFEV1.value) / previousFEV1.value * 100).toFixed(1) 
    : '0';

  const abnormalCount = [
    wbcData.some(d => d.isAbnormal),
    creatinineData.some(d => d.isHigh),
    cfDNAData.some(d => d.isElevated),
  ].filter(Boolean).length;

  // Format date for mobile display
  const formatDateForMobile = (dateStr: string) => {
    const date = new Date(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-border rounded-lg shadow-lg">
          <p className="text-sm text-[#494949] mb-1">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {typeof entry.value === 'number' ? entry.value.toFixed(2) : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Custom tick component for mobile-friendly date labels
  const CustomXAxisTick = ({ x, y, payload }: any) => {
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={10}
          textAnchor="end"
          fill="#494949"
          fontSize={10}
          transform="rotate(-45)"
        >
          {formatDateForMobile(payload.value)}
        </text>
      </g>
    );
  };

  return (
    <div className="space-y-4">
      {/* Summary Insights Card */}
      <Card className="shadow-lg border-primary/20">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex flex-wrap gap-4">
              <div>
                <p className="text-xs text-[#494949]">Last Updated</p>
                <p className="text-sm text-foreground">October 13, 2025</p>
              </div>
              <div className="h-8 w-px bg-border hidden sm:block" />
              <div>
                <p className="text-xs text-[#494949]">Parameters Tracked</p>
                <p className="text-sm text-foreground">7</p>
              </div>
              <div className="h-8 w-px bg-border hidden sm:block" />
              <div>
                <p className="text-xs text-[#494949]">Abnormal Values</p>
                <p className="text-sm text-accent">{abnormalCount}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="border-primary text-primary hover:bg-primary/10"
                onClick={() => setViewMode(viewMode === 'chart' ? 'table' : 'chart')}
              >
                {viewMode === 'chart' ? (
                  <>
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Table View
                  </>
                ) : (
                  <>
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Chart View
                  </>
                )}
              </Button>
              <Button size="sm" className="bg-accent hover:bg-accent/90">
                Generate Clinical Summary
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {viewMode === 'chart' ? (
        <>
          {/* Hemoglobin Trend */}
          <Card className="shadow-lg">
            <CardHeader className="pb-3">
              <CardTitle className="text-primary flex items-center justify-between">
                <span className="text-base">Hemoglobin Trend</span>
                <Button variant="ghost" size="sm">
                  <Download className="w-4 h-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={hemoglobinData} margin={{ bottom: 40 }}>
                    <defs>
                      <linearGradient id="hemoglobin" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#14CAF4" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#14CAF4" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E3" />
                    <XAxis dataKey="date" stroke="#494949" tick={<CustomXAxisTick />} interval={0} />
                    <YAxis domain={[10, 18]} stroke="#494949" tick={{ fontSize: 12 }} label={{ value: 'g/dL', angle: -90, position: 'insideLeft' }} />
                    <Tooltip content={<CustomTooltip />} />
                    <ReferenceArea y1={12} y2={16} fill="#4ade80" fillOpacity={0.1} label="Normal Range" />
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke="#14CAF4"
                      strokeWidth={2}
                      fill="url(#hemoglobin)"
                      name="Hemoglobin"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Normal range: 12–16 g/dL</p>
            </CardContent>
          </Card>

          {/* WBC/TLC Trend */}
          <Card className="shadow-lg">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-primary flex items-center gap-2 text-base">
                  WBC / TLC Trend
                  <Badge variant="outline" className="bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4] text-xs">
                    Last: {latestWBC.value} | Change: {wbcChange >= '0' ? '+' : ''}{wbcChange}%
                  </Badge>
                </CardTitle>
                <Button variant="ghost" size="sm">
                  <Download className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={wbcData} margin={{ bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E3" />
                    <XAxis dataKey="date" stroke="#494949" tick={<CustomXAxisTick />} interval={0} />
                    <YAxis domain={[3000, 14000]} stroke="#494949" tick={{ fontSize: 12 }} label={{ value: 'cells/μL', angle: -90, position: 'insideLeft' }} />
                    <Tooltip content={<CustomTooltip />} />
                    <ReferenceArea y1={4000} y2={11000} fill="#14CAF4" fillOpacity={0.1} label="Normal Zone" />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="#14CAF4"
                      strokeWidth={2}
                      dot={(props: any) => {
                        const point = wbcData[props.index];
                        return (
                          <circle
                            cx={props.cx}
                            cy={props.cy}
                            r={5}
                            fill={point.isAbnormal ? '#F07D13' : '#14CAF4'}
                            stroke="white"
                            strokeWidth={2}
                          />
                        );
                      }}
                      name="TLC"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Normal range: 4,000–11,000 cells/μL • Orange markers indicate abnormal values</p>
            </CardContent>
          </Card>

          {/* Creatinine Trend */}
          <Card className="shadow-lg">
            <CardHeader className="pb-3">
              <CardTitle className="text-primary flex items-center justify-between">
                <span className="text-base">Creatinine Trend</span>
                <Button variant="ghost" size="sm">
                  <Download className="w-4 h-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={creatinineData} margin={{ bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E3" />
                    <XAxis dataKey="date" stroke="#494949" tick={<CustomXAxisTick />} interval={0} />
                    <YAxis domain={[0.4, 2]} stroke="#494949" tick={{ fontSize: 12 }} label={{ value: 'mg/dL', angle: -90, position: 'insideLeft' }} />
                    <Tooltip content={<CustomTooltip />} />
                    <ReferenceArea y1={0.6} y2={1.2} fill="#2762AC" fillOpacity={0.1} label="Normal Range" />
                    <ReferenceLine y={1.5} stroke="#F07D13" strokeDasharray="5 5" label={{ value: 'Alert Threshold', position: 'right', fill: '#F07D13' }} />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="#2762AC"
                      strokeWidth={2}
                      dot={(props: any) => {
                        const point = creatinineData[props.index];
                        return (
                          <g key={`creatinine-dot-${props.index}`}>
                            <circle
                              cx={props.cx}
                              cy={props.cy}
                              r={5}
                              fill="#2762AC"
                              stroke="white"
                              strokeWidth={2}
                            />
                            {point.isHigh && (
                              <g transform={`translate(${props.cx + 10}, ${props.cy - 10})`}>
                                <AlertCircle className="w-4 h-4 text-accent" />
                              </g>
                            )}
                          </g>
                        );
                      }}
                      name="Creatinine"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Normal range: 0.6–1.2 mg/dL • Alert icon shown when &gt;1.5 mg/dL</p>
            </CardContent>
          </Card>

          {/* Tacrolimus Level & Dose */}
          <Card className="shadow-lg">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-primary flex items-center gap-2 text-base">
                  Tacrolimus Level & Dose
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowTacrolimusDose(!showTacrolimusDose)}
                    className="text-xs"
                  >
                    {showTacrolimusDose ? (
                      <>
                        <EyeOff className="w-3 h-3 mr-1" />
                        Hide Dose
                      </>
                    ) : (
                      <>
                        <Eye className="w-3 h-3 mr-1" />
                        Show Dose
                      </>
                    )}
                  </Button>
                </CardTitle>
                <Button variant="ghost" size="sm">
                  <Download className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={tacrolimusData} margin={{ bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E3" />
                    <XAxis dataKey="date" stroke="#494949" tick={<CustomXAxisTick />} interval={0} />
                    <YAxis yAxisId="left" domain={[0, 15]} stroke="#494949" tick={{ fontSize: 12 }} label={{ value: 'Level (ng/mL)', angle: -90, position: 'insideLeft' }} />
                    <YAxis yAxisId="right" orientation="right" domain={[0, 5]} stroke="#494949" tick={{ fontSize: 12 }} label={{ value: 'Dose (mg/day)', angle: 90, position: 'insideRight' }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="level"
                      stroke="#14CAF4"
                      strokeWidth={2}
                      dot={{ fill: '#14CAF4', r: 5 }}
                      name="Tacrolimus Level"
                    />
                    {showTacrolimusDose && (
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="dose"
                        stroke="#F07D13"
                        strokeWidth={2}
                        dot={{ fill: '#F07D13', r: 5, shape: 'square' }}
                        name="Tacrolimus Dose"
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center gap-4 mt-2">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#14CAF4]" />
                  <p className="text-xs text-muted-foreground">Tacrolimus Level</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-accent" />
                  <p className="text-xs text-muted-foreground">Tacrolimus Dose</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Everolimus Level & Dose */}
          <Card className="shadow-lg">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-primary flex items-center gap-2 text-base">
                  Everolimus Level & Dose
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowEverolimusDose(!showEverolimusDose)}
                    className="text-xs"
                  >
                    {showEverolimusDose ? (
                      <>
                        <EyeOff className="w-3 h-3 mr-1" />
                        Hide Dose
                      </>
                    ) : (
                      <>
                        <Eye className="w-3 h-3 mr-1" />
                        Show Dose
                      </>
                    )}
                  </Button>
                </CardTitle>
                <Button variant="ghost" size="sm">
                  <Download className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={everolimusData} margin={{ bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E3" />
                    <XAxis dataKey="date" stroke="#494949" tick={<CustomXAxisTick />} interval={0} />
                    <YAxis yAxisId="left" domain={[0, 10]} stroke="#494949" tick={{ fontSize: 12 }} label={{ value: 'Level (ng/mL)', angle: -90, position: 'insideLeft' }} />
                    <YAxis yAxisId="right" orientation="right" domain={[0, 3]} stroke="#494949" tick={{ fontSize: 12 }} label={{ value: 'Dose (mg/day)', angle: 90, position: 'insideRight' }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="level"
                      stroke="#2762AC"
                      strokeWidth={2}
                      dot={{ fill: '#2762AC', r: 5 }}
                      name="Everolimus Level"
                    />
                    {showEverolimusDose && (
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="dose"
                        stroke="#F07D13"
                        strokeWidth={2}
                        dot={{ fill: '#F07D13', r: 5, shape: 'square' }}
                        name="Everolimus Dose"
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center gap-4 mt-2">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-primary" />
                  <p className="text-xs text-muted-foreground">Everolimus Level</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-accent" />
                  <p className="text-xs text-muted-foreground">Everolimus Dose</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* FEV1 Trend */}
          <Card className="shadow-lg">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-primary flex items-center gap-2 text-base">
                  FEV₁ (Lung Function) Trend
                  <Badge variant="outline" className={`text-xs ${
                    parseFloat(fev1Change) < -10 
                      ? 'bg-accent/10 text-accent border-accent' 
                      : 'bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4]'
                  }`}>
                    Current: {latestFEV1.value || 0}% | Previous: {previousFEV1?.value || 0}% | Change: {fev1Change}%
                  </Badge>
                </CardTitle>
                <Button variant="ghost" size="sm">
                  <Download className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={fev1Data} margin={{ bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E3" />
                    <XAxis dataKey="date" stroke="#494949" tick={<CustomXAxisTick />} interval={0} />
                    <YAxis domain={[70, 90]} stroke="#494949" tick={{ fontSize: 12 }} label={{ value: 'FEV₁ %', angle: -90, position: 'insideLeft' }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="baseline"
                      stroke="#494949"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={false}
                      name="Baseline"
                    />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="#14CAF4"
                      strokeWidth={2}
                      dot={{ fill: '#14CAF4', r: 5 }}
                      name="FEV₁"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              {parseFloat(fev1Change) < -10 && (
                <div className="flex items-center gap-2 mt-2 p-2 bg-accent/10 rounded-lg">
                  <TrendingDown className="w-4 h-4 text-accent" />
                  <p className="text-xs text-accent">Alert: Decline &gt;10% from baseline</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* cfDNA Trend */}
          <Card className="shadow-lg">
            <CardHeader className="pb-3">
              <CardTitle className="text-primary flex items-center justify-between">
                <span className="text-base">cfDNA Trend</span>
                <Button variant="ghost" size="sm">
                  <Download className="w-4 h-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={cfDNAData} margin={{ bottom: 40 }}>
                    <defs>
                      <linearGradient id="cfDNA" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#2762AC" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#2762AC" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E3" />
                    <XAxis dataKey="date" stroke="#494949" tick={<CustomXAxisTick />} interval={0} />
                    <YAxis domain={[0, 2]} stroke="#494949" tick={{ fontSize: 12 }} label={{ value: 'cfDNA %', angle: -90, position: 'insideLeft' }} />
                    <Tooltip content={<CustomTooltip />} />
                    <ReferenceArea y1={1.0} y2={2} fill="#F07D13" fillOpacity={0.1} label="Abnormal Threshold" />
                    <ReferenceLine y={1.0} stroke="#F07D13" strokeDasharray="5 5" label={{ value: 'Threshold', position: 'right', fill: '#F07D13' }} />
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke="#2762AC"
                      strokeWidth={2}
                      fill="url(#cfDNA)"
                      name="cfDNA"
                      dot={(props: any) => {
                        const point = cfDNAData[props.index];
                        return (
                          <g key={`cfdna-dot-${props.index}`}>
                            <circle
                              cx={props.cx}
                              cy={props.cy}
                              r={5}
                              fill={point.isElevated ? '#F07D13' : '#2762AC'}
                              stroke="white"
                              strokeWidth={2}
                            />
                          </g>
                        );
                      }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Abnormal threshold: &gt;1.0% • Alert generated when crossing threshold</p>
            </CardContent>
          </Card>
        </>
      ) : (
        // Table View
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-primary">Lab Results - Table View</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center p-8 text-muted-foreground">
              <BarChart3 className="w-12 h-12 mx-auto mb-4 text-[#14CAF4]" />
              <p>Table view displays all lab values in a tabular format</p>
              <p className="text-sm mt-2">Switch to Chart View to see trends</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}